import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Button,
  LinearProgress,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import ProfileFormSuccess from "./components/ProfileFormSuccess";
import profileFormModel from "./components/formModel/profileFormModel";
import validationSchema from "./components/formModel/validationSchema";
import formInitialValues from "./components/formModel/formInitialValues";
import ClientDetails from "./components/forms/ClientDetails";
import InsuranceCoverage from "./components/forms/InsuranceCoverage";
import TradingInvestment from "./components/forms/TradingAndInvestment";
import LeadApi from "../../crm/leads/api/LeadApi";
import queryClient from "../../../config/queryClient";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import { useProfileFormData } from "./hooks/useProfileData";
import LoaderComponent from "../../../core/common/crmComponents/LoaderComponent";
import MonthlyIncome from "./components/forms/MonthlyIncome";
import MonthlyExpenses from "./components/forms/MonthlyExpences";
import AssetList from "./components/forms/AccestList";
import LiabilityList from "./components/forms/LiabilityList";
import MonthlySaving from "./components/forms/MonthlySaving";
import FormApi from "./api/formApi";
import { BackHand, NavigateBefore, NavigateNext } from "@mui/icons-material";

const steps = [
  "Details",
  "Monthly Income",
  "Monthly Expenses",
  "Accest List",
  "Liability List",
  "Trading & Investment",
  "Insurance Coverage",
];
const { formId, formField } = profileFormModel;

// Validation schema for lead info
const leadInfoValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must not exceed 50 characters")
    .matches(
      /^[a-zA-Z\s'-]+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes",
    )
    .required("First name is required"),
  lastName: Yup.string()
    .trim()
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must not exceed 50 characters")
    .matches(
      /^[a-zA-Z\s'-]+$/,
      "Last name can only contain letters, spaces, hyphens, and apostrophes",
    )
    .required("Last name is required"),
  email: Yup.string()
    .trim()
    .lowercase()
    .email("Please enter a valid email address")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Please enter a valid email address")
    .required("Email is required"),
});

const ProfileStepperForm = ({
  formId,
  formikRef,
  lead,
  setView,
  perFilledData,
  FilledData,
  profilingformStatus = {
    isSaved: false,
    isCompleted: false,
    savePercentage: 0,
    formStepsCompleted: 0,
  },
  onSave,
  onStatusChange,
  onFormValuesChange,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [localStatus, setLocalStatus] = useState(profilingformStatus);
  const [showLeadPrompt, setShowLeadPrompt] = useState(false);
  const [leadInfo, setLeadInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [leadInfoErrors, setLeadInfoErrors] = useState({});

  const initialValues = {
    ...formInitialValues,
    ...(perFilledData && {
      clientDetails: {
        ...formInitialValues.clientDetails,
        registeredMobileNo: perFilledData.mobile,
        registeredEmailAddress: perFilledData.email,
      },
    }),
    ...FilledData,
    insuranceCoverage: {
      ...formInitialValues.insuranceCoverage,
      ...FilledData?.insuranceCoverage,
    },
    ...(leadInfo.firstName && leadInfo.lastName && leadInfo.email
      ? {
          clientDetails: {
            ...formInitialValues.clientDetails,
            name: `${leadInfo.firstName} ${leadInfo.lastName}`,
            email: leadInfo.email,
          },
        }
      : {}),
  };
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const stepperTopOffset = isSmallScreen ? 103 : 90;
  const stepperHeight = isSmallScreen ? 64 : 80;
  const actionBarMinHeight = isSmallScreen ? 88 : 96;
  const pageGutter = isSmallScreen ? 12 : 24;
  const formViewportHeight = `calc(100vh - ${stepperTopOffset + stepperHeight + pageGutter * 2}px)`;

  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const totalSteps = steps.length;
  const navigate = useNavigate();
  const route = all_routes;
  useEffect(() => {
    if (FilledData && FilledData.data === null) {
      setShowLeadPrompt(true);
    } else {
      setShowLeadPrompt(false);
    }
  }, [FilledData]);

  // If form is completed, do not show form
  if (FilledData && FilledData.isCompleted) {
    return (
      <Box sx={{ mt: 10, textAlign: "center" }}>
        <Typography variant="h5" color="success.main">
          Profiling Form is Completed
        </Typography>
      </Box>
    );
  }

  // Lead info prompt
  if (showLeadPrompt) {
    return (
      <Box sx={{ mt: 10, textAlign: "center" }}>
        <Typography variant="h6">
          Please provide your Name and Email to continue
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder="Name"
            value={leadInfo.name}
            onChange={(e) => setLeadInfo({ ...leadInfo, name: e.target.value })}
            style={{ marginBottom: 8, padding: 8, width: 220 }}
          />
          <input
            type="email"
            placeholder="Email"
            value={leadInfo.email}
            onChange={(e) =>
              setLeadInfo({ ...leadInfo, email: e.target.value })
            }
            style={{ marginBottom: 8, padding: 8, width: 220 }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!leadInfo.name || !leadInfo.email}
            onClick={() => setShowLeadPrompt(false)}
          >
            Continue
          </Button>
        </Box>
      </Box>
    );
  }

  function _renderStepContent(step) {
    switch (step) {
      case 0:
        return (
          <ClientDetails formField={formField} perFilledData={perFilledData} />
        );
      case 1:
        return <MonthlyIncome formField={formField} />;
      case 2:
        return <MonthlyExpenses formField={formField} />;
      case 3:
        return <AssetList formField={formField} />;
      case 4:
        return <LiabilityList formField={formField} />;
      case 5:
        return <TradingInvestment formField={formField} />;
      case 6:
        return <InsuranceCoverage formField={formField} />;

      default:
        return <div>Not Found</div>;
    }
  }

  async function _handleSubmit(values, actions, options = {}) {
    const mode = options.mode || (isLastStep ? "submit" : "next");
    try {
      if (mode === "save") {
        // calculate progress
        const percent = Math.round(((activeStep + 1) / totalSteps) * 100);
        const payload = {
          ...values,
          profilingformStatus: {
            isSaved: true,
            isCompleted: false,
            savePercentage: percent,
            formStepsCompleted: activeStep + 1,
          },
        };
        console.log("payload for save:", payload);
        const response = await FormApi.submitProfilingFormData(
          payload,
          payload?._id,
        );
        setLocalStatus((s) => ({
          ...s,
          isSaved: true,
          savePercentage: percent,
          formStepsCompleted: activeStep + 1,
        }));
        if (onStatusChange)
          onStatusChange({
            ...localStatus,
            isSaved: true,
            savePercentage: percent,
            formStepsCompleted: activeStep + 1,
          });
        if (onSave) onSave(response);
        // do not change step on save
      } else if (mode === "submit") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        const payload = {
          ...values,
          profilingformStatus: {
            isSaved: true,
            isCompleted: true,
            savePercentage: 100,
            formStepsCompleted: totalSteps,
          },
          submittedOn: new Date(),
        };
        const response = await FormApi.submitProfilingFormData(
          payload,
          payload?._id,
        );
        setLocalStatus((s) => ({
          ...s,
          isCompleted: true,
          isSaved: true,
          savePercentage: 100,
          formStepsCompleted: totalSteps,
          savedNow: true,
        }));
        if (onStatusChange)
          onStatusChange({
            ...localStatus,
            isCompleted: true,
            isSaved: true,
            savePercentage: 100,
            formStepsCompleted: totalSteps,
            savedNow: true,
          });
      } else {
        // next step
        // save current form values before advancing
        if (onFormValuesChange) onFormValuesChange(values);
        setActiveStep((prev) => prev + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Clear touched fields for next step so user can start fresh
        if (actions && actions.setTouched) actions.setTouched({});
        const percent = Math.round(((activeStep + 1) / totalSteps) * 100);
        setLocalStatus((s) => ({
          ...s,
          savePercentage: percent,
          formStepsCompleted: activeStep + 1,
        }));
        if (onStatusChange)
          onStatusChange({
            ...localStatus,
            savePercentage: percent,
            formStepsCompleted: activeStep + 1,
          });
      }
    } catch (error) {
      console.error("Error submitting profiling form:", error);
    } finally {
      if (actions && actions.setSubmitting) actions.setSubmitting(false);
    }
  }

  function _handleBack() {
    // save current form values before going back
    const currentValues = formikRef.current?.values;
    if (onFormValuesChange && currentValues) onFormValuesChange(currentValues);
    setActiveStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
        backgroundColor: "#f7f9fc",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            mb: 0.5,
            p: isSmallScreen ? 1 : 1,
            px: isMediumScreen ? 1 : 0,
            position: "fixed",
            // top: isSmallScreen ? 103 : 90,
            zIndex: 1000000,
            backgroundColor: "rgb(255, 255, 255)",
            whiteSpace: "nowrap",
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMediumScreen}
            orientation="horizontal"
            sx={{
              gap: 0,
              minWidth: "max-content",
              flexWrap: "nowrap",
              // scale: isSmallScreen ? 0.9 : 1,
              pt: isSmallScreen ? 0 : 0.5,
            }}
          >
            {steps.map((label, index) => (
              <Step key={index} sx={{ minWidth: isSmallScreen ? 80 : 60 }}>
                <StepLabel
                  sx={{
                    p: 0,
                    m: 0,
                    mx: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    ".MuiStepLabel-iconContainer": {
                      pr: 0,
                      mr: 0,
                      display: "flex",
                      alignItems: "center",
                    },
                    ".MuiStepLabel-labelContainer": {
                      mt: isSmallScreen ? 0 : 1,
                      mx: 1,
                    },
                    ".MuiStepLabel-label": {
                      fontSize: isSmallScreen ? "0.6rem" : "0.75rem",
                      lineHeight: 1.2,
                      py: 0,
                      my: 0,
                    },
                    ".MuiStepIcon-root": {
                      fontSize: isSmallScreen ? "1rem" : "1.5rem",
                      m: 0,
                      p: 0,
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {activeStep === steps.length ? (
          <ProfileFormSuccess />
        ) : (
          <Box
            sx={{
              pt: isSmallScreen ? "4vh" : `9vh`,
              // height: formViewportHeight,
              px: isSmallScreen ? 1.5 : 3,
              pb: 3,
            }}
          >
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              validationSchema={currentValidationSchema}
              enableReinitialize={true}
              onSubmit={_handleSubmit}
            >
              {({ isSubmitting, values }) => (
                <Form
                  id={formId}
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 1120,
                      mx: "auto",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                        px: isSmallScreen ? 0.5 : 1,
                        pt: isSmallScreen ? 0.5 : 1,
                        pb: 2,
                      }}
                    >
                      {_renderStepContent(activeStep)}
                    </Box>
                    <Box
                      sx={{
                        position: "fixed",
                        // borderTop: "1px solid #0b0a0a",
                        // backgroundColor: "#000",
                        width: "90vw",
                        maxWidth: 1120,
                        bottom: 0,
                        display: "flex",
                        justifyContent:
                          activeStep === 0 ? "flex-end" : "space-between",
                        alignItems: "center",
                        gap: 2,
                        minHeight: actionBarMinHeight,
                        mt: 1,
                        px: isSmallScreen ? 1 : 0,
                        py: isSmallScreen ? 1 : 1.5,
                        background:
                          "linear-gradient(180deg, rgba(247,249,252,0) 0%, rgba(247,249,252,0.92) 28%, #f7f9fc 100%)",
                        zIndex: 2,
                      }}
                    >
                      {activeStep !== 0 && (
                        <Button
                          onClick={_handleBack}
                          variant="outlined"
                          sx={{
                            fontWeight: "bold",
                            textTransform: "none",
                            px: 4,
                            py: 1,
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                          }}
                        >
                          <NavigateBefore /> Back
                        </Button>
                      )}
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "none",
                          px: 4,
                          py: 1,
                          borderRadius: 2.5,
                          minWidth: isSmallScreen ? 120 : 150,
                          boxShadow: "0 8px 18px rgba(25, 118, 210, 0.28)",
                        }}
                      >
                        {isLastStep ? "Submit" : "Next"} <NavigateNext />
                        {isSubmitting && (
                          <CircularProgress size={24} sx={{ ml: 2 }} />
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProfileStepperForm;
